import { useEffect, useState } from 'react';
import { Layout } from '@/components/layout';
import { PresetCard } from '@/components/domain';
import { Button, EmptyState, ErrorState, Modal, Input, Textarea, Spinner, Tooltip } from '@/components/atoms';
import { useBackButton, useMainButton } from '@/hooks';
import { presets as presetsApi } from '@/api';
import type { ServicePreset } from '@/types/domain';
import { toast } from '@/components/atoms/Toast';
import { logger } from '@/utils/logger';
import styles from './pages.module.css';

export function MyPresetsPage(): JSX.Element {
  useBackButton();
  const [items, setItems] = useState<ServicePreset[] | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState({
    title: '',
    description: '',
    price_eur: 0,
    price_is_from: false,
    duration_minutes: 30,
    category_slug: 'other',
    location_kind: 'onsite' as 'online' | 'delivery' | 'onsite',
  });

  const load = async (): Promise<void> => {
    setError(null);
    try {
      const data = await presetsApi.fetchMyPresets();
      setItems(data.results);
    } catch (err) {
      logger.warn('Presets fetch failed', err);
      setError(err instanceof Error ? err : new Error(String(err)));
      setItems(null);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const create = async (): Promise<void> => {
    try {
      await presetsApi.createPreset(draft);
      toast.success('Додано');
      setOpen(false);
      setDraft({
        title: '',
        description: '',
        price_eur: 0,
        price_is_from: false,
        duration_minutes: 30,
        category_slug: 'other',
        location_kind: 'onsite',
      });
      void load();
    } catch (err) {
      logger.error('Preset create failed', err);
      toast.error('Не вдалось зберегти');
    }
  };

  useMainButton({ text: 'Додати пресет', onClick: () => setOpen(true) });

  return (
    <Layout title="Пресети">
      {error ? (
        <ErrorState onRetry={() => void load()} />
      ) : items === null ? (
        <Spinner center />
      ) : items.length === 0 ? (
        <EmptyState
          icon="presets"
          message="Жодного пресета. Додай — клієнти зможуть бронювати одним тапом."
          actions={<Button onClick={() => setOpen(true)}>Додати пресет</Button>}
        />
      ) : (
        <div className={styles.list}>
          {items.map((p) => (
            <PresetCard key={p.id} preset={p} />
          ))}
        </div>
      )}

      <Modal
        open={open}
        onClose={() => setOpen(false)}
        title="Новий пресет"
        actions={
          <>
            <Button variant="ghost" onClick={() => setOpen(false)}>
              Скасувати
            </Button>
            <Button onClick={create} disabled={!draft.title || !draft.price_eur}>
              Додати
            </Button>
          </>
        }
      >
        <div className={styles.formStack}>
          <Input
            label="Назва"
            value={draft.title}
            onChange={(e) => setDraft({ ...draft, title: e.target.value })}
            maxLength={80}
          />
          <Textarea
            label="Опис"
            value={draft.description}
            onChange={(e) => setDraft({ ...draft, description: e.target.value })}
            maxLength={500}
            rows={3}
          />
          <div className={styles.formRow}>
            <Input
              label="Ціна (€)"
              type="number"
              value={draft.price_eur}
              onChange={(e) => setDraft({ ...draft, price_eur: Number(e.target.value) })}
              min={0}
              step={0.5}
            />
            <div className={styles.fieldWithHint}>
              <Input
                label="Тривалість (хв)"
                type="number"
                value={draft.duration_minutes}
                onChange={(e) =>
                  setDraft({ ...draft, duration_minutes: Number(e.target.value) })
                }
                min={0}
                max={600}
              />
              {/* F-160 D4: hint that 0 = discrete (no calendar slot) */}
              <Tooltip content="Лиши порожнім для discrete замовлень (репетиторство, виїзд)" />
            </div>
          </div>
          {/* F-170 B3.3: optional "від" prefix on the price card. */}
          <label
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 8,
              fontSize: 'var(--font-size-sm)',
              cursor: 'pointer',
            }}
          >
            <input
              type="checkbox"
              checked={draft.price_is_from}
              onChange={(e) => setDraft({ ...draft, price_is_from: e.target.checked })}
            />
            Ціна &laquo;від&raquo;
          </label>
          {/* F-170 B3.2: where the service happens. Radios because the
              three options are mutually exclusive and a chip row in a
              modal felt over-built for three values. */}
          <fieldset
            style={{
              border: 0,
              padding: 0,
              margin: 0,
              display: 'flex',
              flexDirection: 'column',
              gap: 6,
            }}
          >
            <legend
              style={{
                fontSize: 'var(--font-size-sm)',
                color: 'var(--fg-muted)',
                marginBottom: 4,
              }}
            >
              Як надається?
            </legend>
            {(
              [
                { value: 'onsite' as const, label: 'Виїзд / на місці' },
                { value: 'delivery' as const, label: 'З доставкою' },
                { value: 'online' as const, label: 'Онлайн' },
              ]
            ).map((opt) => (
              <label
                key={opt.value}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 8,
                  fontSize: 'var(--font-size-sm)',
                  cursor: 'pointer',
                }}
              >
                <input
                  type="radio"
                  name="location_kind"
                  value={opt.value}
                  checked={draft.location_kind === opt.value}
                  onChange={() => setDraft({ ...draft, location_kind: opt.value })}
                />
                {opt.label}
              </label>
            ))}
          </fieldset>
        </div>
      </Modal>
    </Layout>
  );
}
